// src/context/ConnectivityContext.js
//
// Tracks real backend reachability (not just navigator.onLine, which only
// reflects whether the OS thinks a network interface is up - a device can be
// associated with a LAN/Wi-Fi with zero actual internet, which is the
// realistic failure mode at an exam centre). Drives the offline banner and
// triggers a sync attempt the moment connectivity is confirmed to be back.
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';
import { getQueueCounts } from '../utils/offlineQueue';
import { runSync } from '../utils/syncManager';

export const ConnectivityContext = createContext();

const PING_URL = '/actuator/health';
const PING_INTERVAL_MS = 15000;
const PING_TIMEOUT_MS = 5000;

// Any HTTP response - even a 401/403 - proves the request reached the
// server and came back, which is all "online" needs to mean here. Only a
// genuine network failure (no response object at all) means offline.
async function pingBackend() {
  try {
    await axios.get(PING_URL, { timeout: PING_TIMEOUT_MS });
    return true;
  } catch (err) {
    return !!err.response;
  }
}

export function ConnectivityProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [offlineSince, setOfflineSince] = useState(navigator.onLine ? null : Date.now());
  const [queueCount, setQueueCount] = useState(0);
  const [syncSummary, setSyncSummary] = useState(null);
  const auth = useContext(AuthContext);
  const authRef = useRef(auth);
  const syncingRef = useRef(false);

  useEffect(() => { authRef.current = auth; }, [auth]);

  const refreshQueueCount = useCallback(async () => {
    const { total } = await getQueueCounts();
    setQueueCount(total);
  }, []);

  useEffect(() => {
    refreshQueueCount();
  }, [refreshQueueCount]);

  const attemptSync = useCallback(async () => {
    if (syncingRef.current) return;
    if (authRef.current?.locked) return;
    syncingRef.current = true;
    try {
      const result = await runSync({ lockSession: () => authRef.current?.lockSession?.() });
      if (result) setSyncSummary(result);
      await refreshQueueCount();
    } finally {
      syncingRef.current = false;
    }
  }, [refreshQueueCount]);

  // Confirms a real connectivity change (debounced/verified via a live
  // ping) rather than trusting the browser's online/offline events alone.
  const verifyAndSetStatus = useCallback(async () => {
    const reachable = await pingBackend();
    setIsOnline((prevOnline) => {
      if (reachable && !prevOnline) {
        setOfflineSince(null);
      } else if (!reachable && prevOnline) {
        setOfflineSince(Date.now());
      }
      return reachable;
    });
    return reachable;
  }, []);

  useEffect(() => {
    const handleOnlineEvent = async () => {
      const reachable = await verifyAndSetStatus();
      if (reachable) attemptSync();
    };
    const handleOfflineEvent = () => {
      setIsOnline(false);
      setOfflineSince(Date.now());
    };
    window.addEventListener('online', handleOnlineEvent);
    window.addEventListener('offline', handleOfflineEvent);
    return () => {
      window.removeEventListener('online', handleOnlineEvent);
      window.removeEventListener('offline', handleOfflineEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While offline, poll periodically so a connection that comes back
  // without the browser firing a clean "online" event (common on flaky
  // Wi-Fi) still gets picked up.
  useEffect(() => {
    if (isOnline) return undefined;
    const interval = setInterval(async () => {
      const reachable = await verifyAndSetStatus();
      if (reachable) attemptSync();
    }, PING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isOnline, verifyAndSetStatus, attemptSync]);

  // Once a token-expiry lock (triggered either by the global 401
  // interceptor or by attemptSync itself) is cleared by a successful
  // re-auth, resume any sync that was waiting on it.
  useEffect(() => {
    if (!auth?.locked && isOnline && queueCount > 0) {
      attemptSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.locked]);

  return (
    <ConnectivityContext.Provider value={{ isOnline, offlineSince, queueCount, syncSummary, refreshQueueCount }}>
      {children}
    </ConnectivityContext.Provider>
  );
}
